import { motion } from 'framer-motion';
import { Shield, ChevronRight } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Navbar } from '@/components/layout/Navbar';
import { Footer } from '@/components/landing/Footer';

const lastUpdated = 'April 9, 2026';

const tableOfContents = [
  { id: 'introduction', title: '1. Introduction' },
  { id: 'information-collected', title: '2. Information We Collect' },
  { id: 'how-we-use', title: '3. How We Use Your Information' },
  { id: 'third-party-services', title: '4. Third-Party Services' },
  { id: 'deployment-credentials', title: '5. Cloud Credentials & Deployment Data' },
  { id: 'data-retention', title: '6. Data Retention' },
  { id: 'data-security', title: '7. Data Security' },
  { id: 'your-rights', title: '8. Your Rights' },
  { id: 'cookies', title: '9. Cookies' },
  { id: 'children', title: "10. Children's Privacy" },
  { id: 'changes', title: '11. Changes to This Policy' },
  { id: 'contact', title: '12. Contact' },
];

export function PrivacyPage() {
  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Navbar />

      {/* Hero Section */}
      <section className="relative overflow-hidden py-16 md:py-20">
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-50/60 to-teal-50/60" />
        <div className="container mx-auto px-4 relative">
          <div className="max-w-3xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div className="h-16 w-16 rounded-full gradient-primary flex items-center justify-center mx-auto mb-6">
                <Shield className="h-8 w-8 text-white" />
              </div>
              <h1 className="text-4xl md:text-5xl font-bold mb-4">Privacy Policy</h1>
              <p className="text-muted-foreground">Last updated: {lastUpdated}</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-12 md:py-16 flex-1">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="grid lg:grid-cols-[280px_1fr] gap-8">
              {/* Table of Contents */}
              <motion.aside
                className="lg:sticky lg:top-24 lg:self-start"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5 }}
              >
                <Card>
                  <CardContent className="p-4">
                    <h2 className="font-semibold mb-3 text-sm uppercase tracking-wider text-muted-foreground">
                      Table of Contents
                    </h2>
                    <nav className="space-y-1">
                      {tableOfContents.map((item) => (
                        <button
                          key={item.id}
                          onClick={() => scrollToSection(item.id)}
                          className="flex items-center w-full text-left text-sm py-2 px-3 rounded-md hover:bg-muted transition-colors group"
                        >
                          <ChevronRight className="h-4 w-4 mr-2 text-muted-foreground group-hover:text-emerald-600" />
                          <span className="group-hover:text-emerald-600">{item.title}</span>
                        </button>
                      ))}
                    </nav>
                  </CardContent>
                </Card>
              </motion.aside>

              {/* Main Content */}
              <motion.div
                className="prose prose-slate max-w-none"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
              >
                <Card>
                  <CardContent className="p-6 md:p-8 space-y-8">
                    <section id="introduction">
                      <h2 className="text-2xl font-bold mb-4">1. Introduction</h2>
                      <p className="text-muted-foreground">
                        SetOpenClaw ("we," "us," or "our") respects your privacy. This Privacy Policy
                        explains how we collect, use, and protect information when you use our website
                        (setopenclaw.com), our hosted control plane, and the AI assistant deployments
                        we provision on your behalf.
                      </p>
                    </section>

                    <section id="information-collected">
                      <h2 className="text-2xl font-bold mb-4">2. Information We Collect</h2>
                      <p className="text-muted-foreground mb-4">
                        We collect the following categories of information:
                      </p>
                      <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                        <li>
                          <strong>Account information:</strong> name, email address, password hash,
                          and OAuth identifiers if you sign in with Google.
                        </li>
                        <li>
                          <strong>Billing information:</strong> processed securely through Stripe. We
                          do not store full card numbers on our servers.
                        </li>
                        <li>
                          <strong>Deployment configuration:</strong> the AI provider you choose, the
                          channels you connect (Telegram, Slack, Discord, WhatsApp), the cloud
                          provider you target, and the instance settings you supply.
                        </li>
                        <li>
                          <strong>Cloud provider credentials:</strong> API keys or service-account
                          tokens you supply so we can deploy on your behalf. These are encrypted at
                          rest and used only for the deployments you authorize.
                        </li>
                        <li>
                          <strong>Usage and device information:</strong> IP address, browser type,
                          operating system, and basic interaction events used to operate and improve
                          the service.
                        </li>
                        <li>
                          <strong>Communications:</strong> messages you send via the contact form,
                          support emails, and any feedback you share with our team.
                        </li>
                      </ul>
                    </section>

                    <section id="how-we-use">
                      <h2 className="text-2xl font-bold mb-4">3. How We Use Your Information</h2>
                      <ul className="list-disc pl-6 text-muted-foreground space-y-1">
                        <li>To provision and operate the deployments you request.</li>
                        <li>To process subscription payments and send billing receipts.</li>
                        <li>To monitor instance health and notify you about incidents.</li>
                        <li>To respond to support requests and contact-form submissions.</li>
                        <li>To detect, prevent, and address fraud, abuse, and security issues.</li>
                        <li>To comply with legal obligations.</li>
                      </ul>
                      <p className="text-muted-foreground mt-4">
                        We do not sell, rent, or share your personal information with third parties
                        for marketing purposes.
                      </p>
                    </section>

                    <section id="third-party-services">
                      <h2 className="text-2xl font-bold mb-4">4. Third-Party Services</h2>
                      <p className="text-muted-foreground mb-4">
                        We rely on the following third-party services to operate SetOpenClaw. Each
                        has its own privacy policy governing its use of your information:
                      </p>
                      <ul className="list-disc pl-6 text-muted-foreground space-y-1">
                        <li>
                          <strong>Stripe</strong> for subscription billing and payment processing.
                        </li>
                        <li>
                          <strong>Google OAuth</strong> for optional sign-in with a Google account.
                        </li>
                        <li>
                          <strong>Amazon Web Services (SES)</strong> for transactional email delivery.
                        </li>
                        <li>
                          <strong>Cloud providers you choose</strong> (AWS, Google Cloud, Azure,
                          Hetzner, and others) where your AI assistant instances are deployed.
                        </li>
                        <li>
                          <strong>AI providers you connect</strong> (Anthropic, OpenAI, Google AI,
                          and others) which receive the prompts and conversation data your assistant
                          processes, subject to their own terms.
                        </li>
                      </ul>
                    </section>

                    <section id="deployment-credentials">
                      <h2 className="text-2xl font-bold mb-4">
                        5. Cloud Credentials & Deployment Data
                      </h2>
                      <p className="text-muted-foreground mb-4">
                        To deploy and manage instances on your behalf, SetOpenClaw needs limited,
                        time-bound access to credentials for the cloud provider and AI provider you
                        choose. We commit to:
                      </p>
                      <ul className="list-disc pl-6 text-muted-foreground space-y-1">
                        <li>Encrypting credentials at rest and in transit.</li>
                        <li>
                          Using credentials only to perform the actions you authorize through the
                          dashboard (provisioning, monitoring, backups, teardown).
                        </li>
                        <li>
                          Not copying, retaining, or analyzing the conversation data flowing through
                          your deployed assistant beyond what is needed for monitoring and abuse
                          detection.
                        </li>
                        <li>
                          Permanently revoking and deleting credentials when you remove an instance
                          or disconnect a provider.
                        </li>
                      </ul>
                      <p className="text-muted-foreground mt-4">
                        We strongly recommend that you scope cloud credentials to the minimum
                        permissions required and rotate them periodically.
                      </p>
                    </section>

                    <section id="data-retention">
                      <h2 className="text-2xl font-bold mb-4">6. Data Retention</h2>
                      <p className="text-muted-foreground">
                        We retain your account and configuration data for as long as your account is
                        active. Billing and tax records are retained as required by applicable
                        accounting and tax laws. You can request deletion of your account at any
                        time — see our Data Deletion page for details.
                      </p>
                    </section>

                    <section id="data-security">
                      <h2 className="text-2xl font-bold mb-4">7. Data Security</h2>
                      <p className="text-muted-foreground mb-4">
                        We implement reasonable technical and organizational measures to protect
                        your information, including:
                      </p>
                      <ul className="list-disc pl-6 text-muted-foreground space-y-1">
                        <li>Encryption of data in transit using TLS.</li>
                        <li>Encryption of sensitive credentials at rest.</li>
                        <li>Access controls and authentication on internal systems.</li>
                        <li>Regular security reviews and dependency updates.</li>
                      </ul>
                      <p className="text-muted-foreground mt-4">
                        No method of transmission or storage is 100% secure, and we cannot guarantee
                        absolute security.
                      </p>
                    </section>

                    <section id="your-rights">
                      <h2 className="text-2xl font-bold mb-4">8. Your Rights</h2>
                      <p className="text-muted-foreground mb-4">
                        Depending on your jurisdiction, you may have the right to:
                      </p>
                      <ul className="list-disc pl-6 text-muted-foreground space-y-1">
                        <li>Access the personal data we hold about you.</li>
                        <li>Request correction of inaccurate data.</li>
                        <li>Request deletion of your data.</li>
                        <li>Object to or restrict certain processing activities.</li>
                        <li>Withdraw consent where processing is based on consent.</li>
                      </ul>
                      <p className="text-muted-foreground mt-4">
                        To exercise any of these rights, contact us at{' '}
                        <a
                          href="mailto:support@setopenclaw.com"
                          className="text-emerald-600 hover:underline"
                        >
                          support@setopenclaw.com
                        </a>
                        .
                      </p>
                    </section>

                    <section id="cookies">
                      <h2 className="text-2xl font-bold mb-4">9. Cookies</h2>
                      <p className="text-muted-foreground">
                        SetOpenClaw uses minimal cookies and similar technologies necessary for
                        authentication and core functionality. For details, see our{' '}
                        <a href="/cookies" className="text-emerald-600 hover:underline">
                          Cookie Policy
                        </a>
                        .
                      </p>
                    </section>

                    <section id="children">
                      <h2 className="text-2xl font-bold mb-4">10. Children's Privacy</h2>
                      <p className="text-muted-foreground">
                        SetOpenClaw is not directed to individuals under 16 years of age. We do not
                        knowingly collect personal information from children under 16. If we discover
                        that a child under 16 has provided us with personal information, we will
                        delete it from our servers.
                      </p>
                    </section>

                    <section id="changes">
                      <h2 className="text-2xl font-bold mb-4">11. Changes to This Policy</h2>
                      <p className="text-muted-foreground">
                        We may update this Privacy Policy from time to time. Changes will be posted
                        on this page with an updated date. Continued use of SetOpenClaw after
                        changes constitutes acceptance.
                      </p>
                    </section>

                    <section id="contact">
                      <h2 className="text-2xl font-bold mb-4">12. Contact</h2>
                      <p className="text-muted-foreground">
                        Questions about this policy? Reach us at{' '}
                        <a
                          href="mailto:support@setopenclaw.com"
                          className="text-emerald-600 hover:underline"
                        >
                          support@setopenclaw.com
                        </a>
                        .
                      </p>
                    </section>
                  </CardContent>
                </Card>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
